import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common/PageParts";
import { PageLoader } from "@/components/common/PageLoader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DraftProvider } from "@/components/import/DraftContext";
import { DraftSummary } from "@/components/import/DraftSummary";
import { ItemList } from "@/components/import/ItemList";
import { ImageTray } from "@/components/import/ImageTray";
import { FinalizePanel } from "@/components/import/FinalizePanel";
import { MechanicsPanel } from "@/components/import/MechanicsPanel";

const tabCls = "data-[state=active]:bg-amber-900/40 data-[state=active]:text-amber-100";

export default function ImportDraft() {
  const { draftId } = useParams();
  const { data: draft, isLoading, isError } = useQuery({ queryKey: ["imports", draftId, "draft"], retry: false,
    queryFn: () => api.get(`/imports/${draftId}`).then((r) => r.data) });
  if (isLoading) return <PageLoader />;
  if (isError) return <div className="container-x py-24"><EmptyState title="Import not found" testid="import-not-found"><Link to="/import" className="text-amber-300 hover:underline">Back to the importer</Link></EmptyState></div>;
  const title = draft.set?.name || draft.filename || "Pasted import";
  return (
    <DraftProvider draft={draft}>
      <div className="container-x space-y-8 py-12" data-testid="import-draft-page">
        <PageHeader eyebrow={`Import review · ${draft.status === "draft" ? "private draft" : "finished"}`} title={title} testid="import-draft-header"
          description="Check each card, give every face its finished card image, then import. Nothing is saved to your library until you press Import."
          actions={<Link to="/import" className="btn-ghost" data-testid="import-back-link">All imports</Link>} />
        <DraftSummary />
        <MechanicsPanel />
        <Tabs defaultValue="images">
          <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-[#121620] p-1">
            <TabsTrigger value="images" className={tabCls} data-testid="import-tab-images">Assign images</TabsTrigger>
            <TabsTrigger value="cards" className={tabCls} data-testid="import-tab-cards">All cards</TabsTrigger>
            <TabsTrigger value="finalize" className={tabCls} data-testid="import-tab-finalize">Import</TabsTrigger>
          </TabsList>
          <TabsContent value="images" className="mt-6 grid gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8"><ItemList testid="import-images-list" /></div>
            <div className="lg:col-span-4"><ImageTray /></div>
          </TabsContent>
          <TabsContent value="cards" className="mt-6"><ItemList testid="import-cards-list" /></TabsContent>
          <TabsContent value="finalize" className="mt-6"><FinalizePanel /></TabsContent>
        </Tabs>
      </div>
    </DraftProvider>
  );
}
